import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { FontVariantToken } from '@uipath/apollo-core';
import { ApButton } from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { FieldError } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { ModelsConfigurationOption } from '../../../../services/llm-gateway/LlmGateway.types';
import type { DeprecatedModelInfo } from '../../../../services/llm-gateway/LlmGateway.utils';
import {
    LabelWithTooltip,
    type TooltipProps,
} from '../../subcomponents/LabelWithTooltip';

const useStyles = makeStyles(theme =>
    createStyles({
        triggerBox: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme.spacing(1, 1.5),
            border: `1px solid ${theme.palette.semantic.colorBorderDeEmp}`,
            borderRadius: '4px',
            backgroundColor: theme.palette.semantic.colorBackground,
            cursor: 'pointer',
            width: '350px',
            minHeight: '40px',
            '&:hover': { borderColor: theme.palette.semantic.colorBorder },
        },
        triggerBoxOpen: { borderColor: theme.palette.primary.main },
        triggerBoxDisabled: {
            backgroundColor: theme.palette.action.disabledBackground,
            cursor: 'not-allowed',
        },
        triggerBoxError: { borderColor: theme.palette.error.main },
        dropdownPaper: {
            marginTop: theme.spacing(0.5),
            maxHeight: '280px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: theme.shadows[8],
        },
        searchRow: {
            padding: theme.spacing(0.5, 1),
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
        },
        addButtonRow: {
            padding: theme.spacing(0.5, 1),
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        optionsList: {
            overflow: 'auto',
            maxHeight: '200px',
        },
        optionItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(0.75, 1.5),
            minHeight: '36px',
            cursor: 'pointer',
            '&:hover': { backgroundColor: theme.palette.action.hover },
        },
        optionItemSelected: { backgroundColor: theme.palette.action.selected },
        deprecatedBadge: {
            marginLeft: theme.spacing(1),
            padding: theme.spacing(0.25, 0.75),
            fontSize: '10px',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.action.hover,
            borderRadius: '12px',
            border: `1px solid ${theme.palette.divider}`,
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
        },
        noOptions: {
            padding: theme.spacing(1, 1.5),
            fontStyle: 'italic',
        },
        label: {
            marginBottom: theme.spacing(0.5),
            fontSize: '12px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        errorText: {
            color: theme.palette.error.main,
            marginTop: theme.spacing(0.5),
        },
        warningText: {
            color: theme.palette.error.main,
            marginTop: theme.spacing(0.5),
            fontSize: '12px',
        },
    }),
);

interface ModelComboBoxWithAddProps {
    value?: string;
    options: string[];
    modelsConfigurationOption?: ModelsConfigurationOption;
    onChange: (model: string, isCustomAlias: boolean) => void;
    onBlur?: () => void;
    error?: FieldError;
    disabled?: boolean;
    deprecatedModelsInfo?: Map<string, DeprecatedModelInfo>;
    labelTooltip?: TooltipProps;
}

export const ModelComboBoxWithAdd: React.FC<ModelComboBoxWithAddProps> = ({
    value,
    options,
    modelsConfigurationOption,
    onChange,
    onBlur,
    error,
    disabled,
    deprecatedModelsInfo,
    labelTooltip,
}) => {
    const { formatMessage: translate } = useIntl();
    const classes = useStyles();
    const anchorRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const addInputRef = useRef<HTMLInputElement>(null);

    const [ open, setOpen ] = useState(false);
    const [ searchQuery, setSearchQuery ] = useState('');
    const [ isAddMode, setIsAddMode ] = useState(false);
    const [ newItemValue, setNewItemValue ] = useState('');
    const [ customItems, setCustomItems ] = useState<string[]>([]);
    const [ existingCustomAlias, setExistingCustomAlias ] = useState<string | null>(null);

    const isAdditionMode = modelsConfigurationOption === ModelsConfigurationOption.AnyModelWithOwnAdditions;

    const errorMessage = useMemo(() => {
        if (!error?.message) {
            return undefined;
        }

        return error.message.split(',')[0];
    }, [ error?.message ]);

    const deprecatedModels = useMemo(() => {
        if (!deprecatedModelsInfo) {
            return [];
        }
        return Array.from(deprecatedModelsInfo.keys());
    }, [ deprecatedModelsInfo ]);

    useEffect(() => {
        if (value && !options.includes(value)) {
            setCustomItems(prev => {
                if (prev.includes(value)) {
                    return prev;
                }
                return [ ...prev, value ];
            });
            // Track this as the existing custom alias
            setExistingCustomAlias(value);
        }
    }, [ value, options ]);

    const allOptions = useMemo(() => {
        const combined = [ ...options ];
        customItems.forEach(item => {
            if (!combined.includes(item)) {
                combined.push(item);
            }
        });
        return combined;
    }, [ options, customItems ]);

    // Filter options based on search and sort: non-deprecated first, then deprecated
    const filteredOptions = useMemo(() => {
        const filtered = allOptions.filter(opt =>
            opt.toLowerCase().includes(searchQuery.toLowerCase())
        );

        return filtered.sort((a, b) => {
            const aDeprecated = deprecatedModels.includes(a);
            const bDeprecated = deprecatedModels.includes(b);

            if (aDeprecated === bDeprecated) {
                return 0;
            }
            return aDeprecated ? 1 : -1;
        });
    }, [ allOptions, searchQuery, deprecatedModels ]);

    // Focus search input when opening
    useEffect(() => {
        if (open && !isAddMode && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    }, [ open, isAddMode ]);

    // Focus add input when entering add mode
    useEffect(() => {
        if (isAddMode && addInputRef.current) {
            setTimeout(() => addInputRef.current?.focus(), 50);
        }
    }, [ isAddMode ]);

    const handleOpen = useCallback(() => {
        if (!disabled) {
            setOpen(true);
            setSearchQuery('');
            setIsAddMode(false);
            setNewItemValue('');
        }
    }, [ disabled ]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setSearchQuery('');
        setIsAddMode(false);
        setNewItemValue('');
        onBlur?.();
    }, [ onBlur ]);

    const handleSelect = useCallback((optValue: string) => {
        onChange(optValue, false);
        handleClose();
    }, [ onChange, handleClose ]);

    const handleAddClick = useCallback(() => {
        setIsAddMode(true);
        // If editing an existing alias, pre-populate with the existing value
        setNewItemValue(existingCustomAlias ?? searchQuery);
    }, [ searchQuery, existingCustomAlias ]);

    const handleCancelAdd = useCallback(() => {
        setIsAddMode(false);
        setNewItemValue('');
    }, []);

    const handleConfirmAdd = useCallback(() => {
        const trimmedValue = newItemValue.trim();
        if (trimmedValue) {
            // If editing an existing alias, replace it (keep only one custom alias)
            if (existingCustomAlias) {
                setCustomItems(prev => {
                    // Remove the old alias and add the new one
                    const filtered = prev.filter(item => item !== existingCustomAlias);
                    return [ ...filtered, trimmedValue ];
                });
                setExistingCustomAlias(trimmedValue);
            } else {
                // Add to custom items if not already in options
                if (!options.includes(trimmedValue) && !customItems.includes(trimmedValue)) {
                    setCustomItems(prev => [ ...prev, trimmedValue ]);
                }
                setExistingCustomAlias(trimmedValue);
            }
            onChange(trimmedValue, true);
            handleClose();
        }
    }, [ newItemValue, onChange, handleClose, options, customItems, existingCustomAlias ]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && isAddMode && newItemValue.trim()) {
            e.preventDefault();
            handleConfirmAdd();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            if (isAddMode) {
                handleCancelAdd();
            } else {
                handleClose();
            }
        }
    }, [ isAddMode, newItemValue, handleConfirmAdd, handleCancelAdd, handleClose ]);

    const triggerClassName = [
        classes.triggerBox,
        open && classes.triggerBoxOpen,
        disabled && classes.triggerBoxDisabled,
        error && classes.triggerBoxError,
    ].filter(Boolean).join(' ');

    return (
        <Box>
            {/* Label */}
            <LabelWithTooltip
                label={translate({ id: 'CLIENT_BYO_LLM_NAME' })}
                required
                tooltip={labelTooltip ?? {
                    translationKey: 'CLIENT_BYO_LLM_TOOLTIP_LLM_NAME',
                    placement: 'top',
                }}
                dataCy="model-combobox-label-with-tooltip"
            />

            {/* Trigger button */}
            <Box
                ref={anchorRef}
                onClick={handleOpen}
                className={triggerClassName}
                data-cy="model-combobox-trigger"
            >
                <UiText
                    color={value ? undefined : 'textSecondary'}
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {value ?? translate({ id: 'CLIENT_BYO_LLM_SELECT_MODEL' })}
                </UiText>
                <KeyboardArrowDownIcon
                    sx={{
                        color: 'text.secondary',
                        transform: open ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                    }}
                />
            </Box>

            {/* Dropdown */}
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                style={{
                    zIndex: 1300,
                    width: anchorRef.current?.offsetWidth,
                }}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper
                        className={classes.dropdownPaper}
                        onKeyDown={handleKeyDown}
                    >
                        {/* Row 1: Search or Add input */}
                        <Box className={classes.searchRow}>
                            {isAddMode ? (
                                <InputBase
                                    inputRef={addInputRef}
                                    value={newItemValue}
                                    onChange={(e) => setNewItemValue(e.target.value)}
                                    placeholder={translate({ id: 'CLIENT_BYO_LLM_ENTER_CUSTOM_MODEL_NAME' })}
                                    fullWidth
                                    sx={{ fontSize: '0.875rem' }}
                                    data-cy="model-combobox-add-input"
                                />
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        gap: 0.75,
                                    }}
                                >
                                    <SearchIcon
                                        sx={{
                                            color: 'text.disabled',
                                            fontSize: 18,
                                        }}
                                    />
                                    <InputBase
                                        inputRef={searchInputRef}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder={translate({ id: 'CLIENT_SEARCH' })}
                                        fullWidth
                                        sx={{ fontSize: '0.875rem' }}
                                        data-cy="model-combobox-search-input"
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* Row 2: Add/Edit button or Save/Cancel */}
                        {isAdditionMode && (
                            <Box className={classes.addButtonRow}>
                                {isAddMode ? (
                                    <UiStack
                                        direction="row"
                                        gap={SpacingToken.XS}
                                    >
                                        <ApButton
                                            variant="primary"
                                            size="small"
                                            onClick={handleConfirmAdd}
                                            disabled={!newItemValue.trim()}
                                            data-cy="model-combobox-confirm-add"
                                            label={existingCustomAlias ? translate({ id: 'CLIENT_SAVE' }) : translate({ id: 'CLIENT_BYO_LLM_ADD' })}
                                        />
                                        <ApButton
                                            variant="tertiary"
                                            size="small"
                                            onClick={handleCancelAdd}
                                            data-cy="model-combobox-cancel-add"
                                            label={translate({ id: 'CLIENT_BYO_LLM_CANCEL' })}
                                        />
                                    </UiStack>
                                ) : (
                                    <UiStack
                                        direction="row"
                                        align="center"
                                        gap={SpacingToken.XS}
                                        onClick={handleAddClick}
                                        py={SpacingToken.XS}
                                        style={{ cursor: 'pointer' }}
                                        data-cy="model-combobox-add-new"
                                    >
                                        {existingCustomAlias ? (
                                            <EditIcon
                                                sx={{
                                                    fontSize: 20,
                                                    color: 'primary.main',
                                                }}
                                            />
                                        ) : (
                                            <AddIcon
                                                sx={{
                                                    fontSize: 20,
                                                    color: 'primary.main',
                                                }}
                                            />
                                        )}
                                        <UiText
                                            variant={FontVariantToken.fontSizeMBold}
                                            color="var(--color-primary)"
                                        >
                                            {
                                                existingCustomAlias
                                                    ? translate({ id: 'CLIENT_BYO_LLM_EDIT_CUSTOM_ALIAS' })
                                                    : translate({ id: 'CLIENT_BYO_LLM_ADD_CUSTOM_ALIAS' })
                                            }
                                        </UiText>
                                    </UiStack>
                                )}
                            </Box>
                        )}

                        {/* Options list */}
                        {!isAddMode && (
                            <Box className={classes.optionsList}>
                                {filteredOptions.length === 0 ? (
                                    <UiText
                                        className={classes.noOptions}
                                        color="textSecondary"
                                    >
                                        {translate({ id: 'CLIENT_NO_RESULTS' })}
                                    </UiText>
                                ) : (
                                    filteredOptions.map((opt) => {
                                        const isDeprecated = deprecatedModels.includes(opt);
                                        return (
                                            <Box
                                                key={opt}
                                                onClick={() => handleSelect(opt)}
                                                className={`${classes.optionItem} ${value === opt ? classes.optionItemSelected : ''}`}
                                                data-cy={`model-combobox-option-${opt}`}
                                            >
                                                <UiStack
                                                    direction="row"
                                                    align="center"
                                                >
                                                    <UiText>
                                                        {opt}
                                                    </UiText>
                                                    {isDeprecated && (
                                                        <span className={classes.deprecatedBadge}>
                                                            {translate({ id: 'CLIENT_BYO_LLM_DEPRECATED' })}
                                                        </span>
                                                    )}
                                                </UiStack>
                                                {value === opt && (
                                                    <CheckIcon
                                                        sx={{
                                                            fontSize: 16,
                                                            color: 'primary.main',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>
                        )}
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {/* Error message */}
            {error && (
                <UiText
                    variant={FontVariantToken.fontSizeXsBold}
                    className={classes.errorText}
                >
                    {errorMessage}
                </UiText>
            )}
        </Box>
    );
};
